import type { Folder, SpecialUse } from '@email/shared';
import type { FolderPlacement } from '../../stores/ui.js';

const SPECIAL_ORDER: (SpecialUse | null)[] = [
  'inbox',
  'flagged',
  'sent',
  'drafts',
  'junk',
  'trash',
  'archive',
  'all',
  null,
];

const DEFAULT_MAIN = new Set<SpecialUse>(['inbox', 'flagged', 'sent', 'drafts']);

export type DropEdge = 'before' | 'after';

export function reorderIds(
  ids: readonly number[],
  draggedId: number,
  targetId: number,
  edge: DropEdge,
): number[] {
  if (draggedId === targetId || !ids.includes(draggedId) || !ids.includes(targetId)) {
    return [...ids];
  }
  const reordered = ids.filter((id) => id !== draggedId);
  const targetIndex = reordered.indexOf(targetId);
  reordered.splice(targetIndex + (edge === 'after' ? 1 : 0), 0, draggedId);
  return reordered;
}

export function defaultFolderPlacement(folder: Folder): FolderPlacement {
  return folder.specialUse !== null && DEFAULT_MAIN.has(folder.specialUse) ? 'main' : 'more';
}

export function effectiveFolderPlacement(
  folder: Folder,
  preferences: Record<string, FolderPlacement>,
): FolderPlacement {
  return preferences[String(folder.id)] ?? defaultFolderPlacement(folder);
}

export function sortAccountFolders(folders: Folder[], preferredIds: number[]): Folder[] {
  const preferred = new Map(preferredIds.map((id, index) => [id, index]));
  return [...folders].sort((a, b) => {
    const aPreferred = preferred.get(a.id);
    const bPreferred = preferred.get(b.id);
    if (aPreferred !== undefined && bPreferred !== undefined) return aPreferred - bPreferred;
    if (aPreferred !== undefined) return -1;
    if (bPreferred !== undefined) return 1;
    const special = specialRank(a) - specialRank(b);
    return special || a.path.localeCompare(b.path);
  });
}

export function sortByPreferredIds<T extends { id: number }>(
  items: readonly T[],
  preferredIds: readonly number[],
): T[] {
  const preferred = new Map(preferredIds.map((id, index) => [id, index]));
  return items
    .map((item, index) => ({ item, index, preferred: preferred.get(item.id) }))
    .sort((left, right) => {
      if (left.preferred !== undefined && right.preferred !== undefined) {
        return left.preferred - right.preferred;
      }
      if (left.preferred !== undefined) return -1;
      if (right.preferred !== undefined) return 1;
      return left.index - right.index;
    })
    .map(({ item }) => item);
}

export function folderDisplayName(folder: Folder): string {
  return folder.specialUse === 'inbox' ? 'Inbox' : folder.name;
}

function specialRank(folder: Folder): number {
  const index = SPECIAL_ORDER.indexOf(folder.specialUse);
  return index === -1 ? SPECIAL_ORDER.length : index;
}
